package game

import EntityDef
import com.soywiz.kds.FastArrayList
import com.soywiz.kds.iterators.fastForEach
import com.soywiz.korim.color.RGBA
import game.entity.Entity
import view.Terminal
import kotlin.math.sqrt


enum class TileType(val fgColor: RGBA, val bgColor: RGBA) {
    Floor(CgaColor.WHITE.rgba, CgaColor.YELLOW.rgba),
    Wall(CgaColor.DARK_GRAY.rgba, CgaColor.LIGHT_GRAY.rgba),
    Hole(CgaColor.BLACK.rgba, CgaColor.DARK_GRAY.rgba),
    Water(CgaColor.LIGHT_CYAN.rgba, CgaColor.WHITE.rgba),
    Ice(CgaColor.LIGHT_CYAN.rgba, CgaColor.WHITE.rgba),
    Lava(CgaColor.LIGHT_RED.rgba, CgaColor.YELLOW.rgba),
}

data class Tile(
    var char: Int = 0,
    var fgColor: RGBA = CgaColor.BLACK.rgba,
    var bgColor: RGBA = CgaColor.WHITE.rgba,
    var type: TileType = TileType.Floor
) {
    fun project(terminal: Terminal, col: Int, row: Int) {
        terminal.setChar(col, row, char, fgColor, bgColor)
    }
}

const val ROOM_WIDTH = 51
const val ROOM_HEIGHT = 21

class Room(
    val level: Int = -1,
    val mapX: Int = -1,
    val mapY: Int = -1,
    val exitNorth: String = "",
    val exitSouth: String = "",
    val exitEast: String = "",
    val exitWest: String = "",
    var dark: Boolean = false,
) {
    var id = ""
    val entityDef = FastArrayList<EntityDef>()

    val width = ROOM_WIDTH
    val height = ROOM_HEIGHT
    var loaded = false
    var tiles = List<Tile>(width * height) {
        Tile()
    }

    val shardCount: Int
        get() = entityDef.map {
            shardValue(it.type)
        }.sum()

    val loreCount: Int
        get() = entityDef.count { it.type == "lore" }

    val heartCount: Int
        get() = entityDef.count { it.type == "heart-container" }

    private fun shardValue(entityType: String) = when (entityType) {
        "small-shard" -> 1
        "medium-shard" -> 5
        "large-shard" -> 15
        else -> 0
    }

    operator fun get(col: Int, row: Int) = tiles[row * width + col]
    operator fun set(col: Int, row: Int, tile: Tile) {
        with(get(col, row)) {
            char = tile.char
            fgColor = tile.fgColor
            bgColor = tile.bgColor
            type = tile.type
        }
    }

    private val addedEntities = mutableListOf<Entity>()
    private val removedEntities = mutableListOf<Entity>()
    val entities = mutableListOf<Entity>()
    var discovered = false

    fun getRemainingShards(game: Game) = entityDef.filter {
        !game.isPermanentlyRemoved(it.id)
    }.map {
        shardValue(it.type)
    }.sum()

    fun loreUndiscovered(game: Game) = entityDef.any {
        it.type == "lore" && !game.isLoreDiscovered()
    }

    fun itemUndiscovered(game: Game) = entityDef.any {
        it.type == "item" && !game.isPermanentlyRemoved(it.id)
    }

    fun addEntity(entity: Entity) = addedEntities.add(entity)

    fun getEntityAt(col: Int, row: Int): Entity? =
        entities.firstOrNull { it.contains(col, row) }

    fun getEntityById(entityId: String): Entity? = entities.firstOrNull { it.id == entityId }

    fun removeEntity(entity: Entity) = removedEntities.add(entity)

    fun tick(game: Game) {
        addAndRemoveEntities(game)

        entities.fastForEach { it.tick(game, this) }
        entities.fastForEach {
            if (it.col == game.player.col && it.row == game.player.row) it.onCollideWithPlayer(
                game,
                this
            )
        }

        addAndRemoveEntities(game)
    }

    private fun addAndRemoveEntities(game: Game) {
        while (addedEntities.size != 0) {
            val it = addedEntities[0]
            if (entities.contains(it) == false) {
                entities.add(it)
            }
            it.onInit(game, this)
            addedEntities.removeAt(0)
        }

        while (removedEntities.size != 0) {
            val it = removedEntities[0]
            it.onRemove(game, this)
            entities.remove(it)
            removedEntities.removeAt(0)
        }
    }

    fun render(
        terminal: Terminal,
        colOffset: Int,
        rowOffset: Int,
        startCol: Int = 0,
        startRow: Int = 0,
        width: Int = ROOM_WIDTH,
        height: Int = ROOM_HEIGHT,
        dark: Boolean = false,
        radius: Int = 0,
        lightCol: Int = 0,
        lightRow: Int = 0,
        redden: Boolean = false
    ) {
        render(terminal, colOffset, rowOffset, startCol, startRow, width, height)
        renderEntities(terminal, colOffset, rowOffset, startCol, startRow, width, height)
        if (dark) {
            renderRoomLight(
                terminal,
                colOffset,
                rowOffset,
                startCol,
                startRow,
                width,
                height,
                radius,
                lightCol,
                lightRow
            )
        }
        if (redden) {
            renderRoomRed(
                terminal,
                colOffset,
                rowOffset,
                startCol,
                startRow,
                width,
                height,
            )
        }
    }

    fun renderRoomRed(
        terminal: Terminal,
        colOffset: Int,
        rowOffset: Int,
        startCol: Int = 0,
        startRow: Int = 0,
        width: Int = ROOM_WIDTH,
        height: Int = ROOM_HEIGHT,
    ) {
        for (row in 0 until height) {
            for (col in 0 until width) {
                with(terminal.getChar(col + ROOM_LEFT_COL, row + ROOM_TOP_ROW)) {
                    fgColor = CgaColor.redden(fgColor).rgba
                    bgColor = CgaColor.redden(bgColor).rgba
                }
            }
        }
    }


    fun renderRoomLighter(
        terminal: Terminal,
        amount: Int,
        colOffset: Int = ROOM_LEFT_COL,
        rowOffset: Int = ROOM_TOP_ROW,
        startCol: Int = 0,
        startRow: Int = 0,
        width: Int = ROOM_WIDTH,
        height: Int = ROOM_HEIGHT,
    ) {
        for (row in 0 until height) {
            for (col in 0 until width) {
                with(terminal.getChar(col + ROOM_LEFT_COL, row + ROOM_TOP_ROW)) {
                    fgColor = CgaColor.lighter(fgColor, amount).rgba
                    bgColor = CgaColor.lighter(bgColor, amount).rgba
                }
            }
        }
    }

    fun renderEntities(
        terminal: Terminal,
        colOffset: Int,
        rowOffset: Int,
        startCol: Int,
        startRow: Int,
        width: Int,
        height: Int,
    ) = entities.filter {
        it.col >= startCol &&
                it.col < startCol + width &&
                it.row >= startRow &&
                it.row < startRow + height
    }.fastForEach {
        it.render(terminal, colOffset - startCol, rowOffset - startRow)
    }

    fun renderRoomLight(
        terminal: Terminal,
        colOffset: Int,
        rowOffset: Int,
        startCol: Int,
        startRow: Int,
        width: Int,
        height: Int,
        radius: Int,
        pointCol: Int,
        pointRow: Int
    ) {
        for (y in 0 until height) {
            for (x in 0 until width) {
                val col = x + startCol
                val row = y + startRow
                if (col != pointCol || row != pointRow) {
                    val deltaCol = col - pointCol
                    val deltaRow = row - pointRow
                    val squareCol = deltaCol * deltaCol
                    val squareRow = deltaRow * deltaRow
                    val intensity = (sqrt(squareCol.toDouble() + squareRow.toDouble()) / radius).toInt()
                    with(terminal.getChar(x + colOffset, y + rowOffset)) {
                        fgColor = CgaColor.darker(fgColor, intensity).rgba
                        bgColor = CgaColor.darker(bgColor, intensity).rgba
                    }
                }
            }
        }
    }


    fun renderTiles(terminal: Terminal, colOffset: Int, rowOffset: Int) {
        for (row in 0 until height) {
            for (col in 0 until width) {
                val tile = this[col, row]
                tile.project(terminal, col + colOffset, row + rowOffset)
            }
        }
    }

    private fun clearEntities() {
        entities.clear()
        addedEntities.clear()
        removedEntities.clear()
    }

    fun removePermanently(game: Game, entity: Entity) {
        removeEntity(entity)
        game.addToPermanentlyRemoved(entity.id)
    }

    fun enter(game: Game) {
        discovered = true
        if (!loaded) {
            entityDef.fastForEach {
                if (!game.isPermanentlyRemoved(it.id)) {
                    val entity = createEntity(it)
                    addEntity(entity)
                }
            }

            loaded = true
        } else {
            entityDef.filter {
                it.type == "boulder-1" || it.type == "boulder-2" || it.type == "pot-1" || it.type == "pot-2"
            }.fastForEach {
                val entity = getEntityById(it.id)

                if (entity != null) {
                    removeEntity(entity)
                }

                addEntity(createEntity(it))
            }
        }

        addAndRemoveEntities(game)
    }

    fun exit(game: Game) {
        addAndRemoveEntities(game)
        entities.clear()
        loaded = false
    }

    fun render(
        terminal: Terminal,
        colOffset: Int,
        rowOffset: Int,
        startCol: Int,
        startRow: Int,
        width: Int,
        height: Int
    ) {
        for (y in 0 until height) {
            for (x in 0 until width) {
                val destCol = x + colOffset
                val destRow = y + rowOffset

                val tile = get(startCol + x, startRow + y)

                terminal.setChar(destCol, destRow, tile.char, tile.fgColor, tile.bgColor)
            }
        }
    }
}